import { MaterialItem, MaterialsResponse, MaterialFormat } from '../ad_campaign/types';

export const MATERIAL_FORMATS: MaterialFormat[] = ['square', 'story'];

const MODAL_BODY = '#ad-set-modal-body';

export function $materialPanel(format: MaterialFormat): JQuery {
  return $(`${MODAL_BODY} .material-panel[data-material-format="${format}"]`);
}

export function materialIdAttr(format: MaterialFormat): string {
  return `data-${format}-material-id`;
}

function renderMaterialsPagination($panel: JQuery, format: MaterialFormat, page: number, totalPages: number): void {
  const $container = $panel.find('[data-role="materials-pagination"]').empty();
  if (totalPages <= 1) return;

  const $prev = $('<button type="button" class="btn btn-sm btn-outline-secondary"></button>')
    .text('Prev')
    .prop('disabled', page <= 1)
    .on('click', () => loadMaterials(format, page - 1));

  const $info = $('<span class="mx-2 small text-muted"></span>').text(`Page ${page} of ${totalPages}`);

  const $next = $('<button type="button" class="btn btn-sm btn-outline-secondary"></button>')
    .text('Next')
    .prop('disabled', page >= totalPages)
    .on('click', () => loadMaterials(format, page + 1));

  $container.append($prev, $info, $next);
}

function renderMaterialsGrid($panel: JQuery, format: MaterialFormat, items: MaterialItem[]): void {
  const $grid = $panel.find('[data-role="materials-grid"]').empty();
  if (items.length === 0) {
    $grid.append($('<div class="text-muted text-center py-3 w-100"></div>').text('No materials available'));
    return;
  }

  const attrName = materialIdAttr(format);

  items.forEach(m => {
    const $card = $('<div class="image-card"></div>').attr('data-material-id', String(m.id));
    const $imgWrapper = $('<div class="img-wrapper"></div>');
    if (m.material_type === 'video') {
      $('<video loading="lazy" muted></video>').attr('src', m.src).appendTo($imgWrapper);
    } else {
      $('<img loading="lazy">').attr({ src: m.src, alt: m.name }).appendTo($imgWrapper);
    }
    const $info = $('<div class="card-info"></div>');
    $('<h4 class="card-title"></h4>').text(m.name).appendTo($info);
    $('<span class="card-size"></span>').text(`${m.width} x ${m.height}`).appendTo($info);
    $card.append($imgWrapper, $info);
    $card.on('click', function() {
      const $this = $(this);
      const wasSelected = $this.hasClass('selected');
      $grid.find('.image-card').removeClass('selected');
      if (wasSelected) {
        $(MODAL_BODY).removeAttr(attrName);
      } else {
        $this.addClass('selected');
        $(MODAL_BODY).attr(attrName, $this.attr('data-material-id') ?? '');
      }
    });
    $grid.append($card);
  });

  const selectedId = $(MODAL_BODY).attr(attrName);
  if (selectedId) {
    $grid.find(`.image-card[data-material-id="${selectedId}"]`).addClass('selected');
  }
}

const SPINNER_HTML =
  '<div class="d-flex justify-content-center align-items-center py-4 w-100">' +
    '<div class="spinner-border text-primary" role="status">' +
      '<span class="sr-only">Loading...</span>' +
    '</div>' +
  '</div>';

export function loadMaterials(format: MaterialFormat, page: number = 1, selectId: number | string | null = null): void {
  const $panel = $materialPanel(format);
  if (!$panel.length) return;

  const $grid = $panel.find('[data-role="materials-grid"]');
  const $pagination = $panel.find('[data-role="materials-pagination"]');
  $grid.html(SPINNER_HTML);
  $pagination.empty();

  $.ajax({
    url: '/meta_ads_materials.json',
    method: 'GET',
    data: { page, material_format: format },
    success: function(response: MaterialsResponse) {
      renderMaterialsGrid($panel, format, response.data);
      renderMaterialsPagination($panel, format, response.pagination.page, response.pagination.total_pages);
      if (selectId != null) {
        $grid.find(`.image-card[data-material-id="${selectId}"]`).trigger('click');
      }
    },
    error: function() {
      $grid.html('<div class="text-danger text-center py-3 w-100">Failed to load materials</div>');
      $pagination.empty();
    },
  });
}

const panelObjectUrls: Record<MaterialFormat, string | null> = { square: null, story: null };

function panelFormat($panel: JQuery): MaterialFormat {
  return ($panel.data('material-format') as MaterialFormat) ?? 'square';
}

function showMaterialPreview($panel: JQuery, file: File): void {
  const format = panelFormat($panel);
  const $inner = $panel.find('[data-role="dropzone-inner"]');
  const $preview = $panel.find('[data-role="preview"]');
  const $img = $panel.find('[data-role="preview-img"]');
  const $videoWrap = $panel.find('[data-role="preview-video"]');
  const $videoEl = $panel.find('[data-role="preview-video-el"]');
  const $dropzone = $panel.find('[data-role="dropzone"]');

  if (panelObjectUrls[format]) {
    URL.revokeObjectURL(panelObjectUrls[format] as string);
    panelObjectUrls[format] = null;
  }

  const sizeMb = file.size / (1024 * 1024);
  $panel.find('[data-role="preview-name"]').text(file.name);
  $panel.find('[data-role="preview-size"]').text(`${sizeMb.toFixed(2)} MB`);
  $panel.find('[data-role="preview-name-below"]').text(file.name);

  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onload = ev => {
      const src = ev.target?.result as string;
      $videoWrap.prop('hidden', true);
      $videoEl.prop('hidden', true).removeAttr('src');
      $img.attr('src', src).prop('hidden', false);
    };
    reader.readAsDataURL(file);
  } else {
    const objectUrl = URL.createObjectURL(file);
    panelObjectUrls[format] = objectUrl;
    $img.prop('hidden', true).removeAttr('src');
    $videoWrap.prop('hidden', false);
    $videoEl.attr('src', objectUrl).prop('hidden', false);
  }

  $inner.hide();
  $preview.prop('hidden', false);
  $dropzone.addClass('has-asset').removeClass('is-invalid');
  clearUploadError($dropzone);
}

export function clearMaterialPreview($panel: JQuery): void {
  const format = panelFormat($panel);
  const fileInput = $panel.find('[data-role="file-input"]')[0] as HTMLInputElement | undefined;
  if (fileInput) fileInput.value = '';
  if (panelObjectUrls[format]) {
    URL.revokeObjectURL(panelObjectUrls[format] as string);
    panelObjectUrls[format] = null;
  }
  $panel.find('[data-role="preview-img"]').prop('hidden', true).removeAttr('src');
  $panel.find('[data-role="preview-video"]').prop('hidden', true);
  $panel.find('[data-role="preview-video-el"]').prop('hidden', true).removeAttr('src');
  $panel.find('[data-role="preview"]').prop('hidden', true);
  $panel.find('[data-role="preview-name"]').text('');
  $panel.find('[data-role="preview-size"]').text('');
  $panel.find('[data-role="preview-name-below"]').text('');
  $panel.find('[data-role="dropzone-inner"]').show();
  $panel.find('[data-role="dropzone"]').removeClass('has-asset');
}

function showUploadError($el: JQuery, message: string): void {
  $el.addClass('is-invalid');
  $el.nextAll('.invalid-feedback').remove();
  $el.after(`<p class="invalid-feedback d-block text-danger small mt-1">${message}</p>`);
}

function clearUploadError($el: JQuery): void {
  $el.removeClass('is-invalid');
  $el.nextAll('.invalid-feedback').remove();
}

export function clearUploadErrors($panel: JQuery): void {
  $panel.find('.is-invalid').removeClass('is-invalid');
  $panel.find('.invalid-feedback').remove();
}

export function initMaterialPanel($panel: JQuery): void {
  const format = panelFormat($panel);
  const targetWidth = parseInt(String($panel.data('material-width') ?? ''), 10);
  const targetHeight = parseInt(String($panel.data('material-height') ?? ''), 10);

  $panel.find('.material-tabs').off('click.matTab').on('click.matTab', '.material-tab', function() {
    const tab = $(this).data('tab') as string;
    $panel.find('.material-tabs .material-tab').removeClass('active');
    $(this).addClass('active');
    $panel.find('.material-tab-pane').addClass('d-none');
    $panel.find(`[data-tab-pane="${tab}"]`).removeClass('d-none');
  });

  $panel.find('[data-role="material-name"]').off('input.matName').on('input.matName', function() {
    clearUploadError($(this));
  });

  $panel.find('[data-role="dropzone"]').off('click.matDrop').on('click.matDrop', function(e) {
    if ($(e.target).closest('[data-role="clear-btn"]').length) return;
    if ($(this).hasClass('has-asset')) return;
    ($panel.find('[data-role="file-input"]')[0] as HTMLInputElement).click();
  });

  $panel.find('[data-role="browse-btn"]').off('click.matBrowse').on('click.matBrowse', function(e) {
    e.stopPropagation();
    ($panel.find('[data-role="file-input"]')[0] as HTMLInputElement).click();
  });

  $panel.find('[data-role="clear-btn"]').off('click.matClear').on('click.matClear', function(e) {
    e.stopPropagation();
    clearMaterialPreview($panel);
  });

  $panel.find('[data-role="file-input"]').off('change.matFile').on('change.matFile', function() {
    const file = (this as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      window.alert('Only images and videos are allowed.');
      (this as HTMLInputElement).value = '';
      return;
    }
    showMaterialPreview($panel, file);
  });

  $panel.find('[data-role="dropzone"]')
    .off('dragenter.matDz dragover.matDz dragleave.matDz drop.matDz')
    .on('dragenter.matDz dragover.matDz', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if ($(this).hasClass('has-asset')) return;
      $(this).addClass('dragover');
    })
    .on('dragleave.matDz', function(e) {
      e.preventDefault();
      e.stopPropagation();
      $(this).removeClass('dragover');
    })
    .on('drop.matDz', function(e) {
      e.preventDefault();
      e.stopPropagation();
      $(this).removeClass('dragover');
      if ($(this).hasClass('has-asset')) return;
      const file = (e.originalEvent as DragEvent).dataTransfer?.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        window.alert('Only images and videos are allowed.');
        return;
      }
      const dt = new DataTransfer();
      dt.items.add(file);
      const $fi = $panel.find('[data-role="file-input"]')[0] as HTMLInputElement;
      $fi.files = dt.files;
      showMaterialPreview($panel, file);
    });

  $panel.find('[data-role="upload-btn"]').off('click.matUpload').on('click.matUpload', async function() {
    const $btn = $(this);
    const $name = $panel.find('[data-role="material-name"]');
    const $dropzone = $panel.find('[data-role="dropzone"]');
    const name = ($name.val() ?? '').toString().trim();
    const fileInput = $panel.find('[data-role="file-input"]')[0] as HTMLInputElement;
    const file = fileInput.files?.[0];

    clearUploadErrors($panel);
    let isValid = true;
    if (!name) {
      showUploadError($name, 'Material name is required.');
      isValid = false;
    }
    if (!file) {
      showUploadError($dropzone, 'Please select an image or video file.');
      isValid = false;
    }
    if (!isValid) return;
    if (file!.size > 100 * 1024 * 1024) {
      showUploadError($dropzone, 'File exceeds 100MB.');
      return;
    }

    const materialType: 'image' | 'video' = file!.type.startsWith('video/') ? 'video' : 'image';

    const formData = new FormData();
    formData.append('name', name);
    formData.append('material_type', materialType);
    formData.append('width', String(targetWidth));
    formData.append('height', String(targetHeight));
    formData.append('asset', file!);

    const originalHtml = $btn.html();
    $btn.prop('disabled', true).html(
      '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Uploading...'
    );

    $.ajax({
      url: '/meta_ads_materials',
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      headers: { 'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '' },
      success: function(response: { success: boolean; data: { id: number | string } }) {
        if (response.success && response.data?.id != null) {
          $name.val('');
          clearMaterialPreview($panel);
          clearUploadErrors($panel);
          $panel.find('.material-tab[data-tab="library"]').trigger('click');
          loadMaterials(format, 1, response.data.id);
        } else {
          window.alert('Upload finished but no material id was returned.');
        }
      },
      error: function(xhr) {
        const err = xhr.responseJSON?.error ?? (xhr.responseJSON?.errors ?? []).join(', ') ?? 'Failed to upload material.';
        window.alert(err);
      },
      complete: function() {
        $btn.prop('disabled', false).html(originalHtml);
      },
    });
  });
}
